// Canvas 5: AICX Blueprint (workbook tab 05 Blueprint).
// Steps: riskiest trust moment and human involvement; trust-moment strip; data and platform; AI layers; operating model; vision last.
// Pulls from Canvases 1 to 4. Three cross-canvas checks (Spine, Data, Value to vision) read Pass or Go back.
import { makeDef, G, text, long, pick, tag, calc, fields, table, pull, reference } from './base.js';
import { OPT } from '../spine.js';
import { S, N, T, rows, plural, gap } from '../helpers.js';

// ---------------------------------------------------------------- constants
const STAGE_NOS = ['1', '2', '3', '4', '5', '6'];
const UC_NOS = ['1', '2', '3', '4', '5'];
const DATA_MAX = 5;
const LAYER_CAP = 4;
const VALUE_ROWS = ['customer', 'business', 'efficiency', 'trust'];

const LEVELS_HEAD = ['Level', 'In plain words'];
const LEVELS = [
  ['In the loop', 'A person decides. The AI only recommends.'],
  ['Over the loop', 'The AI decides. A person supervises and can take over.'],
  ['Out of the loop', 'The AI decides alone.'],
];

const READY_HEAD = ['Ready', 'What it means'];
const READY = [
  ['Yes', 'The use case could run on it today.'],
  ['Partial', 'It exists, and it needs cleaning, joining or permission first.'],
  ['No', 'It is not usable yet.'],
];

const BBR_HEAD = ['Ask, in this order (an AICX working rule)', 'If yes'];
const BBR = [
  ['Does something we already run do the job?', 'Reuse'],
  ['Is the job common across organisations, and does a supplier do it well?', 'Buy'],
  ['Does it need data or judgement that only we hold?', 'Build'],
];

const CHECKS_HEAD = ['Check', 'Pass means', 'Go back to'];
const CHECKS = [
  ['Check 1: Spine', 'Your riskiest trust moment has the most S, D, R ticks on Canvas 2.', 'Canvas 2'],
  ['Check 2: Data', 'The data Canvas 3 names for your top three has a Step 3 row, with an owner and a readiness.', 'Canvas 3'],
  ['Check 3: Value to vision', 'The vision names one change the customer would see and one Canvas 4 measure.', 'Canvas 4'],
];

// ---------------------------------------------------------------- local helpers
const factsOf = (up, n) => (up && up[n] && up[n].d && up[n].d.facts) || {};

// the workbook's test for a Phase 1 dependency: the owner is blank, "none", "no owner ..." or "not yet ..."
const noOwner = (owner) => {
  const o = String(owner || '').trim().toLowerCase();
  return o === '' || /^(none|no owner|no one|nobody|not yet|unknown)\b/.test(o);
};

const dataLevel = (up) => {
  const lv = factsOf(up, 1).dims && factsOf(up, 1).dims.data ? factsOf(up, 1).dims.data : null;
  const n = lv ? Number(lv.level) : NaN;
  return Number.isFinite(n) && n > 0 ? { level: n, name: lv.levelName || '' } : null;
};

const ticksText = (s) => (s.sdr && s.sdr.length ? `${s.sdr.join(', ')} (${s.sdr.length} of 3)` : 'None (0 of 3)');

const stageLine = (s) => `${s.n} ${s.stage || '(no name)'}: ${ticksText(s)}`;

const pickLine = (c) => (c ? `${c.name}${c.flag ? '   (guardrail flag: Canvas 6 tests it first)' : ''}` : null);

// the Canvas 3 candidates, or Canvas 2's when Canvas 3 has none yet
const candidatesOf = (up) => {
  const a = factsOf(up, 3).candidates;
  const b = factsOf(up, 2).candidates;
  return (a && a.length ? a : b || []).filter((c) => c && c.name);
};

// the rows of the AI layers table: the top pick, then the second pick, then the other candidates in order
const layerList = (up) => {
  const f3 = factsOf(up, 3);
  const cands = candidatesOf(up);
  const topId = f3.topPick && f3.topPick.id;
  const secId = f3.secondPick && f3.secondPick.id;
  const first = [topId, secId].map((id) => cands.find((c) => c.id === id)).filter(Boolean);
  const rest = cands.filter((c) => !first.includes(c));
  return [...first, ...rest].map((c) => ({
    id: c.id,
    label: c.name,
    cand: c,
    isTop: c.id === topId,
    sub: [c.stage, c.id === topId ? 'top pick' : c.id === secId ? 'second pick' : ''].filter(Boolean).join(', ') || undefined,
  }));
};

// does the vision's measure name one of the Canvas 4 measures? A short name such as "Customer Effort Score" counts.
const namesMeasure = (typed, measures) => {
  const t = String(typed || '').trim().toLowerCase();
  if (t.length < 3) return false;
  return measures.some((m) => {
    const x = m.toLowerCase();
    return x === t || x.includes(t) || t.includes(x);
  });
};

const VAGUE_HUMAN = /^(a |the )?(human|person|people|someone|staff)( is| are| will be)?( involved| in the loop| there)?\.?$/i;

// ---------------------------------------------------------------- NorthStar (master spec section 6; printed canvas; fixture)
const NORTHSTAR = {
  'riskiest.stage': '3',
  'involve.level': 'in the loop',
  'involve.for': 'declines (over the loop for routine approvals)',
  'strip.ai': 'Scores the application and drafts a plain-language reason',
  'strip.human': 'A trained service officer reviews and calls on declines',
  'strip.customer': 'Read the reason and ask for a person',
  ...T('data', [
    { item: 'KYC and account data', source: 'Core banking system', owner: 'Core banking lead', ready: 'Yes', tag: 'E', uc: '1' },
    { item: 'Uploaded documents', source: 'Document store', owner: 'Operations lead', ready: 'Partial', tag: 'E', uc: '2' },
    { item: 'Activation and churn signals', source: 'Churn model data', owner: 'No owner', ready: 'Partial', tag: 'G', uc: '5' },
    { item: 'Single customer profile', source: 'Five systems', owner: 'No owner', ready: 'No', tag: 'G' },
  ]),
  remedy: 'Start unifying the customer profile now spread across five systems.',
  ...T(
    'layers',
    [
      {
        does: 'Pre-fills the form from what NorthStar already holds, and reads the documents of customers with no national digital identity record',
        bbr: 'build',
        order: 'first',
      },
      {
        does: 'Scores the application, then drafts a plain-language reason for every credit decline, and a clear next step where the law limits what can be said',
        bbr: 'build',
        order: 'later',
      },
      { does: 'Guides the upload, shows its status', bbr: 'buy', order: 'later' },
      { does: 'Verifies identity', bbr: 'buy', order: 'later' },
    ],
    ['r1', 'r4', 'r2', 'r3'],
  ),
  'op.owner': 'Head of CX (proposed)',
  'op.use': "Head of Retail Banking, on the forum's recommendation",
  'op.model': 'AI decision forum',
  'op.changes': "AI decision forum, on the journey owner's request",
  'op.forum': 'AI decision forum',
  'op.seats': 'Risk and Data',
  'vision.text': 'Open an account in one sitting, on any channel, with no repeated questions.',
  'vision.change': 'No repeated questions',
  'vision.measure': 'Customer Effort Score',
  'so.reviewer': 'IT and Data',
  'so.objection': 'Two of your four data items have no owner. Who is on the hook?',
  'so.evidence': 'Both are Phase 1 dependencies: Canvas 5 data rows 3 and 4',
  'so.owner': 'Head of IT and Data',
  'so.status': 'Open',
};

// ---------------------------------------------------------------- the definition
export default makeDef(5, {
  short: 'Blueprint',
  purpose: 'Design what has to exist behind the experience: the riskiest trust moment, the data, the AI layers, who decides, and the vision.',
  howTo: [
    'Start at the riskiest trust moment on Canvas 2 and say how much a human does there. Then write what the AI, the human and the customer each do.',
    'List the data the journey needs, with its source, its owner and how ready it is. An item with no owner becomes a Phase 1 dependency on Canvas 8.',
    'Name the AI layers, starting with your top pick, and choose build, buy or reuse for each. Set a role for each decision. Write the vision last.',
    'Three checks run on their own: Spine, Data and Value to vision. A Go back sends you to the canvas that holds the answer. Never adjust this canvas to fit.',
  ],
  results: [
    { id: 'layers', label: 'AI layers named', rule: 'Count the layers in Step 4.' },
    { id: 'data', label: 'Data items with an owner and a readiness', rule: 'Of the data items in Step 3.' },
    { id: 'deps', label: 'Phase 1 dependencies', rule: 'Count the data items with no owner in Step 3.' },
    { id: 'check1', label: 'Check 1: Spine', rule: 'Your riskiest trust moment has the most ticks on Canvas 2.' },
    { id: 'check2', label: 'Check 2: Data', rule: 'The data Canvas 3 names for your top three has a Step 3 row, with an owner and a readiness.' },
    { id: 'check3', label: 'Check 3: Value to vision', rule: 'The vision names one change the customer would see and one Canvas 4 measure.' },
  ],
  steps: [
    // ------------------------------------------------------------ Step 1
    {
      hint: 'Choose the stage with the most S, D, R ticks on Canvas 2. It may belong to your second pick. Then set the level of human involvement there.',
      blocks: [
        pull('p1', 'From earlier canvases', [
          { id: 'journey', label: 'Priority journey', ref: 'Canvas 2', no: 2, get: (up) => factsOf(up, 2).journey || null },
          { id: 'top', label: 'Top pick', ref: 'Canvas 3', no: 3, get: (up) => pickLine(factsOf(up, 3).topPick) },
          { id: 'second', label: 'Second pick', ref: 'Canvas 3', no: 3, get: (up) => pickLine(factsOf(up, 3).secondPick) },
          {
            id: 'stages',
            label: 'Stages and their S, D, R ticks',
            ref: 'Canvas 2',
            no: 2,
            get: (up) => {
              const list = factsOf(up, 2).stages || [];
              return list.length ? list.map(stageLine).join('\n') : null;
            },
            note: 'Type the number of the stage you choose below.',
          },
          {
            id: 'most',
            label: 'Most ticks on Canvas 2',
            ref: 'Canvas 2',
            no: 2,
            get: (up) => {
              const list = factsOf(up, 2).stages || [];
              const max = Math.max(0, ...list.map((s) => (s.sdr || []).length));
              if (!list.length) return null;
              if (max === 0) return 'No stage is ticked S, D or R';
              const best = list.find((s) => (s.sdr || []).length === max);
              return `${best.n} ${best.stage}`;
            },
          },
        ]),
        fields([
          pick('riskiest.stage', 'Riskiest trust moment (stage number on Canvas 2)', STAGE_NOS, {
            required: true,
            w: 2,
            guide: G(
              'Pick the number of the stage with the most S, D, R ticks. The stages and their ticks are listed above.',
              '3, which is Get approved or declined (D and R).',
              'The riskiest trust moment is the stage with the most S, D, R ticks on Canvas 2. It may belong to your second pick, not your top pick.',
              'If the stage you want does not have the most ticks, correct Canvas 2. Never adjust Canvas 5 to fit.',
            ),
          }),
          calc('riskiest.name', 'The stage you chose', {
            w: 2,
            rule: 'the name of that stage on Canvas 2',
            guide: G('Worked out for you from Canvas 2. Check that it is the stage you mean.', 'Get approved or declined'),
          }),
          calc('riskiest.ticks', 'S, D, R ticks there (from Canvas 2)', {
            w: 2,
            rule: 'copied from Canvas 2',
            guide: G('Worked out for you from Canvas 2.', 'D, R (2 of 3)'),
          }),
          calc('riskiest.human', 'Human role there (from Canvas 2)', {
            w: 2,
            rule: 'copied from Canvas 2',
            guide: G('Worked out for you from Canvas 2.', 'A trained service officer calls on declines'),
          }),
          pick('involve.level', 'Level of human involvement', OPT.involve, {
            required: true,
            w: 2,
            guide: G(
              'Pick how much the human does at this one moment: in the loop, over the loop or out of the loop.',
              'in the loop',
              'In the loop: a person decides and the AI only recommends. Over the loop: the AI decides and a person can take over. Out of the loop: the AI decides alone.',
              '"In the loop" is a design choice, not a safe default. A person who cannot see why the AI recommended something adds little.',
            ),
          }),
          text('involve.for', 'For what (optional)', {
            w: 2,
            guide: G(
              'Say which cases the level applies to. Use it when the level differs by case.',
              'declines (over the loop for routine approvals)',
            ),
          }),
          calc('check1', 'Check 1: Spine check', {
            w: 4,
            rule: 'Pass when the stage you chose has the most ticks on Canvas 2',
            guide: G(
              'Worked out for you. It compares the stage you chose with the stage with the most ticks on Canvas 2.',
              'Pass',
              'A failed check sends you to Canvas 2, the source canvas. Never adjust this canvas to fit.',
            ),
          }),
        ]),
        reference('The three levels of human involvement', LEVELS_HEAD, LEVELS),
      ],
    },
    // ------------------------------------------------------------ Step 2
    {
      hint:
        'Write three lines for the riskiest trust moment. Name the role: "a human is involved" does not count. ' +
        'The customer can edit, decline, ask for a person or read the reason.',
      blocks: [
        fields([
          long('strip.ai', 'What AI does', {
            required: true,
            w: 4,
            rows: 2,
            guide: G(
              'One line on the job the AI does at this moment. Name the job, never the product.',
              'Scores the application and drafts a plain-language reason',
              undefined,
              'A product name is not a job. Say what the AI does for the customer.',
            ),
          }),
          long('strip.human', 'What a human adds (name the role)', {
            required: true,
            w: 4,
            rows: 2,
            guide: G(
              'Name the role and say what that person adds.',
              'A trained service officer reviews and calls on declines',
              undefined,
              '"A human is involved" does not count. Name the role. A person who signs in seconds, without seeing why, adds little.',
            ),
          }),
          long('strip.customer', 'What the customer can do', {
            required: true,
            w: 4,
            rows: 2,
            guide: G(
              'What the customer can do at this moment: edit, decline, ask for a person, or read the reason.',
              'Read the reason and ask for a person',
              undefined,
              'If the customer can do nothing, say so. That is a finding for Canvas 6.',
            ),
          }),
        ]),
      ],
    },
    // ------------------------------------------------------------ Step 3
    {
      hint: 'Add a row for each data item the journey needs. An item with no owner becomes a Phase 1 dependency on Canvas 8: write "No owner" and the canvas marks it.',
      blocks: [
        pull('p3', 'From earlier canvases', [
          {
            id: 'level',
            label: 'Data and AI level',
            ref: 'Canvas 1',
            no: 1,
            get: (up) => {
              const lv = dataLevel(up);
              return lv ? `Level ${lv.level}${lv.name ? `, ${lv.name}` : ''}` : null;
            },
            note: 'At level 2 or lower you write one remediation action below.',
          },
          {
            id: 'named',
            label: 'Data named for your top three',
            ref: 'Canvas 3',
            no: 3,
            get: (up) => {
              const list = (factsOf(up, 3).candidates || []).filter((c) => c.top3 && c.data);
              return list.length ? list.map((c) => `Use case ${c.n}, ${c.name}: ${c.data}`).join('\n') : null;
            },
            note: 'Each of these needs a row below, with an owner and a readiness (Check 2).',
          },
        ]),
        table('data', {
          title: 'Data items',
          rows: { min: 1, max: DATA_MAX, start: 4, label: 'Data item' },
          pdf: 'table',
          columns: [
            text('item', 'Data item', {
              required: true,
              w: 2,
              guide: G(
                'What the journey needs, in plain words.',
                'KYC and account data',
                undefined,
                'One row for each data item. "Customer data" is too wide: name the item.',
              ),
            }),
            text('source', 'Source system', {
              required: true,
              w: 2,
              guide: G(
                'The system it lives in, by name.',
                'Core banking system',
                undefined,
                '"In various systems" is not a source. Which system would you open first?',
              ),
            }),
            text('owner', 'Data owner (a role)', {
              required: true,
              w: 2,
              placeholder: 'A role, or: No owner',
              guide: G(
                'A role that answers for the data. If nobody does, write "No owner".',
                'Core banking lead',
                'A blank owner is a finding. An item with no owner becomes a Phase 1 dependency on Canvas 8.',
                'Write a role, not a person. Do not leave it blank: write "No owner" so the finding is visible.',
              ),
            }),
            pick('ready', 'Ready', OPT.ready, {
              required: true,
              w: 1,
              guide: G(
                'Pick how ready the data is.',
                'Yes for KYC and account data.',
                'Yes: the use case could run on it today. Partial: it exists, and it needs cleaning, joining or permission first. No: it is not usable yet.',
              ),
            }),
            tag('tag', 'Tag', {
              required: true,
              w: 1,
              guide: G(
                'How well you can prove the readiness: M Measured, E Estimated, G Guess.',
                'E',
                'E or G goes to the Evidence Gaps list.',
              ),
            }),
            pick('uc', 'For use case no.', UC_NOS, {
              w: 1,
              guide: G(
                'The Canvas 3 number of the use case this data serves.',
                '1 for KYC and account data.',
                'Check 2 reads this column: every data item Canvas 3 names for your top three needs a row with its number.',
                'Leave it blank for data that serves no single use case.',
              ),
            }),
            calc('dep', 'Phase 1 dependency', {
              w: 1,
              rule: 'Phase 1 when the owner is blank, "none", "no owner" or "not yet"',
              guide: G(
                'Worked out for you. It reads Phase 1 when the item has no owner.',
                'Phase 1 for Activation and churn signals.',
                'Each Phase 1 item is a dependency on Canvas 8.',
              ),
            }),
          ],
        }),
        fields([
          long('remedy', 'Remediation action (when Data and AI is level 2 or lower)', {
            w: 4,
            rows: 2,
            required: ({ up }) => {
              const lv = dataLevel(up);
              return !!lv && lv.level <= 2;
            },
            guide: G(
              'One action that moves the data forward. Required when Data and AI on Canvas 1 is level 2 or lower.',
              'Start unifying the customer profile now spread across five systems.',
              undefined,
              'One action, not a programme. Name the first step.',
            ),
          }),
          calc('remedy.status', 'Does the remediation action apply?', {
            w: 4,
            rule: 'Data and AI on Canvas 1 at level 2 or lower needs one action',
            guide: G('Worked out for you from Canvas 1.', 'Data and AI is level 2: write one action'),
          }),
          calc('check2', 'Check 2: Data check', {
            w: 4,
            rule: 'Pass when every data item Canvas 3 names for your top three has a row here, with an owner and a readiness',
            guide: G(
              'Worked out for you. It compares the data Canvas 3 names for your top three with the rows above.',
              'Pass',
              'A failed check sends you to Canvas 3, the source canvas, or you add the missing row here.',
            ),
          }),
        ]),
        reference('How ready is the data?', READY_HEAD, READY),
      ],
    },
    // ------------------------------------------------------------ Step 4
    {
      hint: 'The top pick is the first layer. Choose the others from the Canvas 2 candidates: leave a candidate blank if it is not a layer. Name the job, never the product.',
      blocks: [
        table('layers', {
          title: 'AI layers',
          rows: { from: (up) => layerList(up).map(({ id, label, sub }) => ({ id, label, sub })) },
          pdf: 'cards',
          columns: [
            long('does', 'What it does in the journey', {
              required: layerNeeded,
              w: 4,
              rows: 2,
              guide: G(
                'The job this layer does in the journey, in one line.',
                'Pre-fills the form from what NorthStar already holds, and reads the documents of customers with no national digital identity record',
                undefined,
                'Name the job, never the product.',
              ),
            }),
            calc('type', 'Type (from Canvas 2)', {
              w: 1,
              rule: 'copied from the candidate on Canvas 2',
              guide: G('Worked out for you from Canvas 2: scores, writes or acts.', 'writes'),
            }),
            pick('bbr', 'Build, buy or reuse', OPT.bbr, {
              required: layerNeeded,
              w: 1,
              guide: G(
                'Ask in this order: does something we already run do the job (reuse)? Is it common, and does a supplier do it well (buy)? Does it need what only we hold (build)?',
                'build for Pre-fill and document reading.',
                'Whichever you choose, you stay accountable for it, and someone has to run it a year from now.',
                'Do not choose buy to avoid a decision. If you bought it, who answers for it when it is wrong?',
              ),
            }),
            pick('order', 'Order', OPT.order, {
              required: layerNeeded,
              w: 1,
              guide: G(
                'Pick when the layer comes: first, next or later.',
                'first for the top pick; later for the others.',
                'The top pick is the first layer.',
              ),
            }),
          ],
        }),
        reference('Build, buy or reuse: a rule of thumb', BBR_HEAD, BBR),
      ],
    },
    // ------------------------------------------------------------ Step 5
    {
      hint: 'Set a role for each decision. Write "none" if nobody does: that is a finding. If the same body approves all three, check that this is deliberate.',
      blocks: [
        pull('p5', 'From Canvas 2', [
          {
            id: 'owner',
            label: 'Journey owner named on Canvas 2 today',
            ref: 'Canvas 2',
            no: 2,
            get: (up) => factsOf(up, 2).owner || null,
          },
        ]),
        fields([
          text('op.owner', 'Journey owner (a role)', {
            required: true,
            w: 2,
            guide: G(
              'One role that owns the whole journey.',
              'Head of CX (proposed)',
              'One journey owner and one forum. Write roles, never names of people.',
              'Nobody owns a full journey today? Propose a role and say so, as NorthStar does.',
            ),
          }),
          text('op.forum', 'The AI decision forum', {
            required: true,
            w: 2,
            guide: G(
              'The one body that convenes to decide on AI for this journey. Write "none" if there is none.',
              'AI decision forum',
              undefined,
              'A forum that meets only when called is not yet a forum.',
            ),
          }),
          text('op.use', 'Approves the use case', {
            required: true,
            w: 2,
            guide: G(
              'The role or body that says yes to the use case. Write "none" if nobody does: that is a finding.',
              "Head of Retail Banking, on the forum's recommendation",
            ),
          }),
          text('op.model', 'Approves the model', {
            required: true,
            w: 2,
            guide: G(
              'The role or body that approves the model before it goes live. Write "none" if nobody does.',
              'AI decision forum',
            ),
          }),
          text('op.changes', 'Approves changes', {
            required: true,
            w: 2,
            guide: G(
              'The role or body that approves changes after launch. Write "none" if nobody does.',
              "AI decision forum, on the journey owner's request",
            ),
          }),
          text('op.seats', 'Standing seats on the forum (optional)', {
            w: 2,
            guide: G(
              'The roles that hold a standing seat, so that Risk and Data are at the table from the start.',
              'Risk and Data',
            ),
          }),
        ]),
      ],
    },
    // ------------------------------------------------------------ Step 6
    {
      hint: 'Write one sentence the customer would recognise. A vision with no Canvas 4 measure is a slogan.',
      blocks: [
        pull('p6', 'The four measures on Canvas 4', [
          { id: 'customer', label: 'Customer', ref: 'Canvas 4', no: 4, get: (up) => (factsOf(up, 4).rows || {}).customer?.measure || null },
          { id: 'business', label: 'Business', ref: 'Canvas 4', no: 4, get: (up) => (factsOf(up, 4).rows || {}).business?.measure || null },
          { id: 'efficiency', label: 'AI and efficiency', ref: 'Canvas 4', no: 4, get: (up) => (factsOf(up, 4).rows || {}).efficiency?.measure || null },
          { id: 'trust', label: 'Trust and Human', ref: 'Canvas 4', no: 4, get: (up) => (factsOf(up, 4).rows || {}).trust?.measure || null },
        ]),
        fields([
          long('vision.text', 'Vision, in one sentence', {
            required: true,
            w: 4,
            rows: 2,
            guide: G(
              'One sentence the customer would recognise, written last.',
              'Open an account in one sitting, on any channel, with no repeated questions.',
              undefined,
              'A vision in the bank\'s words ("a seamless digital experience") is a slogan. Use what the customer would say.',
            ),
          }),
          text('vision.change', 'One customer-visible change', {
            required: true,
            w: 2,
            guide: G(
              'The one change a customer would see.',
              'No repeated questions',
              'The vision passes when it names one change a customer would see and one measure on Canvas 4.',
              'A change inside the bank, such as "a single data platform", is not one the customer sees.',
            ),
          }),
          text('vision.measure', 'One Canvas 4 measure', {
            required: true,
            w: 2,
            guide: G(
              'The one measure from Canvas 4 that would show the vision is real. Use its name as it appears on Canvas 4.',
              'Customer Effort Score',
              'A vision with no measure on Canvas 4 is a slogan. Go back to Canvas 4 for the measure.',
              'Do not invent a measure here. If Canvas 4 does not hold it, go back to Canvas 4.',
            ),
          }),
          calc('check3', 'Check 3: Value to vision check', {
            w: 4,
            rule: 'Pass when the vision names one customer-visible change and one Canvas 4 measure',
            guide: G(
              'Worked out for you from the three boxes above and the measures on Canvas 4.',
              'Pass',
              'A failed check sends you to Canvas 4 for the measure, or to Canvas 2 for the change.',
            ),
          }),
        ]),
        reference('The three checks', CHECKS_HEAD, CHECKS),
      ],
    },
  ],

  // ---------------------------------------------------------------- derive
  derive(v, up) {
    const f2 = factsOf(up, 2);
    const f3 = factsOf(up, 3);
    const f4 = factsOf(up, 4);
    const stages = f2.stages || [];
    const calcOut = {};

    // Step 1: the riskiest trust moment
    const stageNo = N(v, 'riskiest.stage');
    const stage = stageNo === null ? null : stages.find((s) => s.n === stageNo) || null;
    const maxTicks = Math.max(0, ...stages.map((s) => (s.sdr || []).length));
    const bestStage = maxTicks > 0 ? stages.find((s) => (s.sdr || []).length === maxTicks) : null;
    const humanThere = stage ? stage.human || '(none named on Canvas 2)' : '';
    const level = S(v, 'involve.level');
    calcOut['riskiest.name'] = stage ? stage.stage : '';
    calcOut['riskiest.ticks'] = stage ? ticksText(stage) : '';
    calcOut['riskiest.human'] = humanThere;
    let check1 = '';
    if (stageNo !== null) {
      if (!stage) check1 = 'Go back: Canvas 2. Choose one of its stages';
      else if (maxTicks === 0) check1 = 'Go back: Canvas 2. No stage there is ticked S, D or R';
      else if ((stage.sdr || []).length === maxTicks) check1 = 'Pass';
      else check1 = `Go back: Canvas 2. ${bestStage.stage} has more ticks: correct the ticks there, or choose that stage`;
    }
    calcOut.check1 = check1;
    const cands = candidatesOf(up);
    const inStage = stage ? cands.filter((c) => c.stage && c.stage === stage.stage) : [];
    const useCase = (inStage.find((c) => c.flag) || inStage.find((c) => c.pick === 'second') || inStage.find((c) => c.pick === 'top') || inStage[0] || {}).name || '';

    // Step 3: data and platform
    const dataRows = rows(v, 'data');
    for (let n = 1; n <= DATA_MAX; n += 1) {
      const r = dataRows.find((x) => x.n === n);
      calcOut[`data.r${n}.dep`] = r && r.item ? (noOwner(r.owner) ? 'Phase 1' : '') : '';
    }
    const items = dataRows.filter((r) => r.item);
    const owned = items.filter((r) => !noOwner(r.owner) && r.ready);
    const ownerless = items.filter((r) => noOwner(r.owner));
    const lv = dataLevel(up);
    const remedy = S(v, 'remedy');
    calcOut['remedy.status'] = !lv
      ? ''
      : lv.level <= 2
        ? remedy
          ? 'Filled'
          : `Data and AI is level ${lv.level}: write one action`
        : `Not needed: Data and AI is level ${lv.level}`;
    // Check 2: every data item Canvas 3 names for the top three has a row here, with an owner and a readiness
    const needs = (f3.candidates || []).filter((c) => c.top3 && c.data);
    const missingUc = needs.filter((c) => !items.some((r) => r.uc === String(c.n) && !noOwner(r.owner) && r.ready));
    let check2 = '';
    if (needs.length || items.length) {
      check2 = missingUc.length
        ? `Go back: Canvas 3 names data for use case ${missingUc.map((c) => c.n).join(', ')}. ` +
          'Add a row here with that number, an owner and a readiness, or correct Canvas 3'
        : 'Pass';
    }
    calcOut.check2 = check2;

    // Step 4: AI layers
    const layerRows = layerList(up);
    const layers = [];
    layerRows.forEach((l) => {
      const k = (c) => `layers.${l.id}.${c}`;
      calcOut[k('type')] = l.cand.type || '';
      const does = S(v, k('does'));
      const bbr = S(v, k('bbr'));
      const order = S(v, k('order'));
      if (does || bbr || order) layers.push({ n: layers.length + 1, id: l.id, name: l.label, type: l.cand.type || '', does, bbr, order });
    });

    // Step 6: the vision
    const vision = S(v, 'vision.text');
    const change = S(v, 'vision.change');
    const measure = S(v, 'vision.measure');
    const measures = VALUE_ROWS.map((r) => ((f4.rows || {})[r] || {}).measure).filter(Boolean);
    let check3 = '';
    if (vision) {
      if (!measure || !namesMeasure(measure, measures)) check3 = 'Go back: Canvas 4. A vision with no Canvas 4 measure is a slogan';
      else if (!change) check3 = 'Go back: Canvas 2. Name the one change the customer would see';
      else check3 = 'Pass';
    }
    calcOut.check3 = check3;

    const passWord = (c) => (c === '' ? '' : c === 'Pass' ? 'Pass' : 'Go back');
    const results = {
      layers: layers.length ? `${layers.length} of ${LAYER_CAP}` : '',
      data: items.length ? `${owned.length} of ${items.length}` : '',
      deps: items.length ? String(ownerless.length) : '',
      check1: passWord(check1),
      check2: passWord(check2),
      check3: passWord(check3),
    };

    // evidence: every data item tagged E or G
    const evidence = [];
    items.forEach((r) => {
      if (r.tag === 'E' || r.tag === 'G') {
        evidence.push(gap(`Data item: ${r.item} (${r.source || 'no source named'}${r.ready ? `, ready: ${r.ready}` : ''})`, r.tag, `Canvas 5, data item ${r.n}`));
      }
    });

    // warnings
    const warnings = [];
    if (check1 && check1 !== 'Pass') warnings.push({ step: 's1', text: check1 });
    if (stage && level && level !== 'out of the loop' && humanThere === '(none named on Canvas 2)') {
      warnings.push({ step: 's1', text: 'Canvas 2 names no human role for this moment: add it there.' });
    }
    if (VAGUE_HUMAN.test(S(v, 'strip.human'))) {
      warnings.push({ step: 's2', text: 'Name the role. "A human is involved" does not count.' });
    }
    if (ownerless.length) {
      const k = ownerless.length;
      warnings.push({
        step: 's3',
        text:
          `${k} data ${plural(k, 'item')} ${k === 1 ? 'has' : 'have'} no owner: ${k === 1 ? 'it becomes a Phase 1 dependency' : 'they become Phase 1 dependencies'} ` +
          'on Canvas 8. Name an owner, or leave the gap visible.',
      });
    }
    if (check2 && check2 !== 'Pass') warnings.push({ step: 's3', text: check2 });
    if (layers.length > LAYER_CAP) {
      warnings.push({ step: 's4', text: `Name at most ${LAYER_CAP} layers. Keep the layers that matter first, and leave the rest for the Later list on Canvas 8.` });
    }
    const approvals = [S(v, 'op.use'), S(v, 'op.model'), S(v, 'op.changes')];
    if (approvals.every(Boolean) && approvals.every((a) => a.toLowerCase() === approvals[0].toLowerCase())) {
      warnings.push({ step: 's5', text: 'The same body approves all three decisions: check that this is deliberate.' });
    }
    const noApprover = [...approvals, S(v, 'op.forum')].filter((a) => a && /^none\b/i.test(a)).length;
    if (noApprover) {
      warnings.push({ step: 's5', text: `${noApprover} ${plural(noApprover, 'decision')} with no role or forum: that is a finding. Raise it with your sponsor.` });
    }
    if (check3 && check3 !== 'Pass') warnings.push({ step: 's6', text: check3 });

    const facts = {
      riskiest: { stage: stage ? stage.stage : '', n: stage ? stage.n : null, sdr: stage ? [...(stage.sdr || [])] : [], useCase },
      involvement: level,
      involvementFor: S(v, 'involve.for'),
      strip: { ai: S(v, 'strip.ai'), human: S(v, 'strip.human'), customer: S(v, 'strip.customer') },
      vision,
      change,
      measure,
      layers: layers.map(({ n, name, type, bbr, order, does }) => ({ n, name, type, bbr, order, does })),
      dataItems: items.map((r) => ({
        item: r.item,
        source: r.source,
        owner: noOwner(r.owner) ? '' : r.owner,
        ready: r.ready,
        tag: r.tag,
        uc: r.uc || '',
      })),
      ownerless: ownerless.length,
      remediation: remedy,
      operating: {
        journeyOwner: S(v, 'op.owner'),
        forum: S(v, 'op.forum'),
        seats: S(v, 'op.seats'),
        approvesUse: S(v, 'op.use'),
        approvesModel: S(v, 'op.model'),
        approvesChanges: S(v, 'op.changes'),
      },
      checks: { spine: passWord(check1), data: passWord(check2), value: passWord(check3) },
    };

    return { calc: calcOut, results, facts, evidence, warnings, blockers: [] };
  },
  NORTHSTAR,
});

// a layer row must be filled when it is the top pick, or when the learner has started it
function layerNeeded({ row, rowInfo, up }) {
  const topId = factsOf(up, 3).topPick && factsOf(up, 3).topPick.id;
  const started = !!(row && (row.does || row.bbr || row.order));
  return started || !!(rowInfo && topId && rowInfo.id === topId);
}
